import { EstimatedTradeCostConfigState } from 'widgets/estimatedTradeCost/widget/store/EstimatedTradeCostConfigState';
import { useEstimatedTradeCostWidgetConfigSlice } from 'widgets/estimatedTradeCost/widget/store/configslice';
import { useWidgetConfigUpdater } from 'widgets/WidgetSettingsEditing/useWidgetConfigUpdater';
import React, { useCallback, useMemo, useState } from 'react';
import {
    getBenchmarkTooltip,
    getProfileTooltip,
    StrikeBenchmark,
} from 'widgets/estimatedTradeCost/components/options';
import WidgetSettingsNumericValueEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsNumericValueEditor';

import './EstimatedTradeCostWidgetSettings.scss';
import { TimeInput } from '@instinet-analytics/semantic-ui-components';
import { Time } from '@instinet-analytics/core-lib';
import { IconTooltip } from 'utils/components/IconTooltip';
import { BenchmarkDropdown } from 'widgets/estimatedTradeCost/components/BenchmarkDropdown';
import { ProfileDropdown } from 'widgets/estimatedTradeCost/components/ProfileDropdown';

export function EstimatedTradeCostWidgetSettings({
    config: propsConfig,
    onChange,
}: {
    config?: EstimatedTradeCostConfigState;
    onChange?: (nextDelta: Partial<EstimatedTradeCostConfigState>) => void;
}) {
    const { config: configSliceConfig, dispatchConfigChanged } =
        useEstimatedTradeCostWidgetConfigSlice();

    const { debouncedApplyWidgetConfigChange } = useWidgetConfigUpdater(
        dispatchConfigChanged,
        onChange
    );

    const config = propsConfig ?? configSliceConfig;

    const [benchmark, setBenchmark] = useState<string | undefined>(
        config.benchmark ?? ''
    );
    const [profile, setProfile] = useState<string | undefined>(
        config.profile ?? ''
    );
    const [strikeTime, setStrikeTime] = useState(config.strikeTime);

    const onBenchmarkChanged = useCallback(
        (nextBenchmark: string, nextProfile: string) => {
            setBenchmark(nextBenchmark);
            setProfile(nextProfile);
            const delta: Partial<EstimatedTradeCostConfigState> = {
                benchmark: nextBenchmark,
                profile: nextProfile,
            };
            debouncedApplyWidgetConfigChange(delta);
        },
        [setBenchmark, setProfile, debouncedApplyWidgetConfigChange]
    );

    const onProfileChanged = useCallback(
        (nextValue: string) => {
            setProfile(nextValue);
            const delta: Partial<EstimatedTradeCostConfigState> = {
                profile: nextValue,
            };
            debouncedApplyWidgetConfigChange(delta);
        },
        [debouncedApplyWidgetConfigChange, setProfile]
    );

    const onStrikeTimeChanged = useCallback(
        (nextValue: Time | undefined) => {
            if (!nextValue) {
                return;
            }
            setStrikeTime(nextValue);
            const delta: Partial<EstimatedTradeCostConfigState> = {
                strikeTime: nextValue,
            };
            debouncedApplyWidgetConfigChange(delta);
        },
        [debouncedApplyWidgetConfigChange, setStrikeTime]
    );

    const isStrike = benchmark === StrikeBenchmark;

    const benchmarkTooltip = useMemo(
        () => getBenchmarkTooltip(benchmark),
        [benchmark]
    );
    const profileTooltip = useMemo(() => getProfileTooltip(profile), [profile]);

    return (
        <div className="EstimatedTradeCostWidgetSettings">
            <div className="group ">
                <div>
                    <div>Benchmark</div>

                    <div className="BenchmarkDropdown control-with-help">
                        <BenchmarkDropdown
                            benchmark={benchmark}
                            profile={profile}
                            onChanged={onBenchmarkChanged}
                        />
                        <IconTooltip>{benchmarkTooltip}</IconTooltip>
                    </div>
                </div>
                <div>
                    <div>Profile</div>

                    <div className="ProfileDropdown control-with-help">
                        <ProfileDropdown
                            benchmark={benchmark}
                            profile={profile}
                            onChanged={onProfileChanged}
                        />
                        <IconTooltip>{profileTooltip}</IconTooltip>
                    </div>
                </div>
                <div className="control-with-help">
                    <WidgetSettingsNumericValueEditor
                        propertyName={'minPartLit'}
                        config={config}
                        label={'Min Part Lit'}
                        min={0}
                        max={100}
                        onChange={debouncedApplyWidgetConfigChange}
                    />
                    <IconTooltip>
                        Min participation across Lit markets
                    </IconTooltip>
                </div>
                <div className="control-with-help">
                    <WidgetSettingsNumericValueEditor
                        propertyName={'maxPartLit'}
                        config={config}
                        label={'Max Part Lit'}
                        min={0}
                        max={100}
                        onChange={debouncedApplyWidgetConfigChange}
                    />
                    <IconTooltip>
                        Max participation across Lit markets
                    </IconTooltip>
                </div>
                {isStrike && (
                    <div className="control-with-help">
                        <div className="label">Strike Time</div>
                        <TimeInput
                            time={strikeTime}
                            onChange={onStrikeTimeChanged}
                            className="strike-time"
                        />
                        <IconTooltip>
                            Max participation across Lit markets
                        </IconTooltip>
                    </div>
                )}
            </div>
        </div>
    );
}
